/**
 * The Dock — a WAI-ARIA toolbar on the bottom, left or right of the desktop:
 *
 *   [All screens] | Pinned screens | open, unpinned screens · a few recent |
 *   minimised windows · minimised editors
 *
 * Its SIZE is the user's (a slider in the right-click menu and in Desktop &
 * Dock settings) and never changes by itself; a Dock fuller than the desktop
 * scrolls instead. Everything that follows the pointer is a direct DOM update
 * per animation frame, never a re-render:
 *   - magnification grows the tiles near the pointer, measured against their
 *     REST positions (so growth cannot feed back into itself and jitter);
 *   - the tooltip stays up while the pointer travels along the Dock and
 *     follows the hovered tile, instead of hiding and re-showing between them;
 *   - dragging the Dock's background moves the Dock with the pointer and
 *     previews the edge it will land on;
 *   - dragging a pinned tile reorders it, its neighbours sliding aside to show
 *     where it will drop (Alt+Shift+arrows from the keyboard).
 * Right-click (or the context-menu key on the Dock) offers Position on screen,
 * Size, Magnification, Close all windows and Desktop & Dock settings.
 */
import { Check, LayoutGrid, X } from 'lucide-react'
import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
  type CSSProperties, type KeyboardEvent, type ReactNode, type RefObject,
} from 'react'
import { tw } from '../../../testHooks'
import { EditorMark, UnsavedDot, editorTitle } from '../../nav/parts/EditorsList'
import { useEntryMenu } from '../../nav/parts/ShellLists'
import { useDragReorder } from '../../nav/parts/useDragReorder'
import { NavIcon } from '../../nav/navIcons'
import { S } from '../../nav/tokens'
import type { RunNavItem } from '../../nav/types'
import type { EditorSession, ShellNavModel } from '../../nav/useShellNav'
import { LIGHTS, glass, menuPanel } from './styles'
import { shownEditorOf } from './MenuBar'
import { DOCK_SIDES, DOCK_SIZE_MAX, DOCK_SIZE_MIN, type DockSide } from './Settings'

/** Recent screens offered in the Dock beside the pinned and open ones. */
const DOCK_RECENT = 3
/** Biggest a magnified tile grows, and how far (px) the effect reaches. */
const MAG_MAX = 1.6
const MAG_REACH = 150
const GAP = 8

export interface DockProps {
  model: ShellNavModel
  accent: string
  allOpen: boolean
  onToggleAll: () => void
  launcherRef: RefObject<HTMLButtonElement | null>
  side: DockSide
  magnify: boolean
  /** Tile size in px (the user's slider). */
  size: number
  onSide: (side: DockSide) => void
  onMagnify: (on: boolean) => void
  onSize: (px: number) => void
  onSettings: () => void
  onCloseAll: () => void
  /** Windows minimised to the Dock (open screens not on the desktop). */
  minimised: RunNavItem[]
  onRestoreWindow: (item: RunNavItem) => void
}

export function Dock({
  model, accent, allOpen, onToggleAll, launcherRef, side, magnify, size, onSide, onMagnify, onSize,
  onSettings, onCloseAll, minimised, onRestoreWindow,
}: DockProps) {
  const ref = useRef<HTMLDivElement>(null)
  const tipRef = useRef<HTMLSpanElement>(null)
  const entryMenu = useEntryMenu(model)
  const vertical = side !== 'bottom'
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const [tip, setTip] = useState<{ key: string; text: string } | null>(null)
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const [moveTarget, setMoveTarget] = useState<DockSide | null>(null)
  const [overflowing, setOverflowing] = useState(false)

  const current = model.current
  const openIds = new Set(model.openScreens.map((i) => i.id))
  const pinnedIds = new Set(model.pinned.map((e) => e.item.id))
  const running = model.openScreens.filter((i) => !pinnedIds.has(i.id) && !minimised.some((m) => m.id === i.id))
  const recent = model.recent
    .filter((e) => !pinnedIds.has(e.item.id) && !openIds.has(e.item.id))
    .slice(0, DOCK_RECENT)
  const shown = shownEditorOf(model)
  const editors = model.editorsEnabled ? model.editors.filter((s) => s.id !== shown?.id) : []
  const thumbW = vertical ? Math.round(size * 1.45) : Math.round(size * 2.15)

  // Every key in DOM order — the roving tab stop falls back to the launcher.
  const keys = [
    'all',
    ...model.pinned.map((e) => `pin:${e.item.id}`),
    ...running.map((i) => `run:${i.id}`),
    ...recent.map((e) => `rec:${e.item.id}`),
    ...minimised.map((i) => `min:${i.id}`),
    ...editors.flatMap((s) => [`ed:${s.id}`, `edx:${s.id}`]),
  ]
  const activeKey = focusKey && keys.includes(focusKey) ? focusKey : 'all'

  const reduceMotion = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const magOn = magnify && !reduceMotion && !overflowing

  // ── Fits, or scrolls? (A full Dock scrolls; it never shrinks itself.) ──
  const itemCount = keys.length
  useLayoutEffect(() => {
    const el = ref.current
    const parent = el?.parentElement
    if (!el || !parent) return undefined
    const check = () => {
      const room = vertical ? parent.clientHeight - 40 : parent.clientWidth - 120
      // Rest length: every tile at its base size, thumbnails as drawn.
      let length = 24
      const items = [...el.children] as HTMLElement[]
      items.forEach((c, i) => {
        const mag = c.hasAttribute('data-mac-mag')
        length += (mag ? size : (vertical ? c.offsetHeight : c.offsetWidth)) + (i ? GAP : 0)
      })
      const over = length > room
      setOverflowing((prev) => (prev === over ? prev : over))
    }
    check()
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(check)
    ro.observe(parent)
    return () => ro.disconnect()
  }, [itemCount, size, vertical])

  // ── Magnification ──────────────────────────────────────────────────
  const rest = useRef<Map<HTMLElement, number> | null>(null)
  const raf = useRef(0)
  const pointer = useRef<number | null>(null)
  const tipKeyRef = useRef<string | null>(null)
  tipKeyRef.current = tip?.key ?? null
  const sideRef = useRef(side)
  sideRef.current = side
  const sizeRef = useRef(size)
  sizeRef.current = size
  const mags = () => [...(ref.current?.querySelectorAll<HTMLElement>('[data-mac-mag]') || [])]

  const placeTip = useCallback(() => {
    const t = tipRef.current
    const k = tipKeyRef.current
    if (!t || !k) return
    const el = ref.current?.querySelector<HTMLElement>(`[data-mac-dock-key="${CSS.escape(k)}"]`)
    if (!el) return
    const r = el.getBoundingClientRect()
    const s = sideRef.current
    if (s === 'left') { t.style.left = `${r.right + 12}px`; t.style.top = `${r.top + r.height / 2}px` }
    else if (s === 'right') { t.style.left = `${r.left - 12}px`; t.style.top = `${r.top + r.height / 2}px` }
    else { t.style.left = `${r.left + r.width / 2}px`; t.style.top = `${r.top - 10}px` }
  }, [])
  const measureRest = useCallback(() => {
    const m = new Map<HTMLElement, number>()
    const v = sideRef.current !== 'bottom'
    for (const el of mags()) {
      const r = el.getBoundingClientRect()
      m.set(el, v ? r.top + r.height / 2 : r.left + r.width / 2)
    }
    rest.current = m
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const paint = useCallback(() => {
    raf.current = 0
    const px = pointer.current
    const m = rest.current
    const base = sizeRef.current
    for (const el of mags()) {
      let sc = 1
      const c = px !== null && m ? m.get(el) : undefined
      if (px !== null && c !== undefined) {
        const d = Math.min(1, Math.abs(px - c) / MAG_REACH)
        // Cosine falloff: full growth at the pointer, easing smoothly to none.
        sc = 1 + (MAG_MAX - 1) * (1 + Math.cos(Math.PI * d)) / 2
      }
      const v = `${Math.round(base * sc * 10) / 10}px`
      el.style.width = v
      el.style.height = v
      const ico = el.querySelector<HTMLElement>('[data-mac-ico]')
      if (ico) ico.style.transform = sc === 1 ? '' : `scale(${sc})`
    }
    placeTip()
  }, [placeTip]) // eslint-disable-line react-hooks/exhaustive-deps
  const schedule = () => { if (!raf.current) raf.current = requestAnimationFrame(paint) }
  const resetMag = useCallback((instant = false) => {
    pointer.current = null
    rest.current = null
    cancelAnimationFrame(raf.current)
    raf.current = 0
    for (const el of mags()) {
      const ico = el.querySelector<HTMLElement>('[data-mac-ico]')
      if (instant) { el.style.transition = 'none'; if (ico) ico.style.transition = 'none' }
      el.style.width = ''
      el.style.height = ''
      if (ico) ico.style.transform = ''
      if (instant) {
        void el.offsetWidth // apply now, then let later changes animate again
        el.style.transition = ''
        if (ico) ico.style.transition = ''
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { resetMag(true) }, [magOn, size, side, resetMag])
  useEffect(() => () => cancelAnimationFrame(raf.current), [])

  // ── Tooltip ────────────────────────────────────────────────────────
  useLayoutEffect(() => { placeTip() }, [tip, placeTip])
  const showTip = (key: string, text: string) => setTip((p) => (p && p.key === key && p.text === text ? p : { key, text }))
  const itemProps = (key: string, text: string) => ({
    'data-mac-dock-key': key,
    tabIndex: key === activeKey ? 0 : -1,
    onFocus: () => { setFocusKey(key); showTip(key, text) },
    onBlur: (e: React.FocusEvent<HTMLElement>) => {
      if (!ref.current?.contains(e.relatedTarget as Node)) setTip(null)
    },
    // Enter only: moving from one tile to the next re-targets the tooltip
    // (it hides when the pointer leaves the Dock), so it never flickers.
    onPointerEnter: () => showTip(key, text),
  })

  // ── Reorder pinned tiles by dragging ───────────────────────────────
  const [reordering, setReordering] = useState(false)
  const { bind } = useDragReorder({
    axis: vertical ? 'y' : 'x', containerRef: ref, disabled: model.pinned.length < 2,
    onDragState: (on) => { setReordering(on); if (on) setTip(null) },
    onMove: (key, to) => {
      const from = model.pinned.findIndex((e) => e.item.id === key)
      const it = model.pinned[from]?.item
      if (it && from !== to) model.movePin(it, to - from)
    },
  })

  // ── Moving the Dock: drag its background to an edge ────────────────
  const moveRef = useRef<{ x: number; y: number; target: DockSide; started: boolean } | null>(null)
  const sideAt = (x: number): DockSide => {
    const box = ref.current?.parentElement?.getBoundingClientRect()
    if (!box) return side
    const f = (x - box.left) / Math.max(1, box.width)
    return f < 0.28 ? 'left' : f > 0.72 ? 'right' : 'bottom'
  }
  const onPointerDownCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    // A press on a pinned tile may start a reorder, which measures the tiles:
    // measure them at REST, not magnified.
    if ((e.target as HTMLElement).closest('[data-reorder-key]')) resetMag(true)
  }
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // The Dock's own background (between and around the tiles) moves the Dock.
    if (e.button !== 0 || (e.target as HTMLElement).closest('button, [data-reorder-key]')) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    moveRef.current = { x: e.clientX, y: e.clientY, target: side, started: false }
  }
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const m = moveRef.current
    if (!m) {
      if (!magOn || reordering) return
      if (!rest.current) measureRest()
      pointer.current = vertical ? e.clientY : e.clientX
      schedule()
      return
    }
    const dx = e.clientX - m.x
    const dy = e.clientY - m.y
    if (!m.started) {
      if (Math.hypot(dx, dy) < 6) return
      m.started = true
      resetMag(true)
      setTip(null)
      document.body.style.userSelect = 'none'
      setMoveTarget(m.target)
    }
    const el = ref.current
    if (el) { el.style.transition = 'none'; el.style.translate = `${dx}px ${dy}px` }
    const t = sideAt(e.clientX)
    if (t !== m.target) { m.target = t; setMoveTarget(t) }
  }
  const endMove = (commit: boolean) => {
    const m = moveRef.current
    moveRef.current = null
    if (!m) return
    document.body.style.userSelect = ''
    setMoveTarget(null)
    const el = ref.current
    if (m.started && commit && m.target !== side) {
      // The Dock re-mounts on its new side (keyed by side) with its own entrance.
      if (el) el.style.translate = ''
      onSide(m.target)
      return
    }
    // Back to where it was, smoothly.
    if (el) { el.style.transition = 'translate .2s ease'; el.style.translate = '' }
  }

  const refocus = (key: string) => {
    // A reordered tile is moved in the DOM, which can drop focus: put it back.
    requestAnimationFrame(() => {
      ref.current?.querySelector<HTMLElement>(`[data-mac-dock-key="${CSS.escape(key)}"]`)?.focus()
    })
  }
  const movePinned = (item: RunNavItem, delta: number) => {
    model.movePin(item, delta)
    refocus(`pin:${item.id}`)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
      e.preventDefault()
      const r = ref.current?.getBoundingClientRect()
      if (r) setMenu({ x: r.left + 12, y: Math.max(8, r.top - 300) })
      return
    }
    const els = [...(ref.current?.querySelectorAll<HTMLElement>('[data-mac-dock-key]') || [])]
    const i = els.indexOf(document.activeElement as HTMLElement)
    if (i < 0) return
    const go = (j: number) => { e.preventDefault(); els[Math.max(0, Math.min(els.length - 1, j))]?.focus() }
    switch (e.key) {
      case vertical ? 'ArrowDown' : 'ArrowRight': go(i + 1); break
      case vertical ? 'ArrowUp' : 'ArrowLeft': go(i - 1); break
      case 'Home': go(0); break
      case 'End': go(els.length - 1); break
      default:
    }
  }

  const sep = (k: string) => (
    <span key={k} aria-hidden style={vertical
      ? { alignSelf: 'stretch', height: 1, margin: '2px 6px', flexShrink: 0, background: S.borderStrong }
      : { alignSelf: 'stretch', width: 1, margin: '6px 2px', flexShrink: 0, background: S.borderStrong }} />
  )
  const dot = (isCurrent: boolean): CSSProperties => (side === 'bottom'
    ? { bottom: -7, left: '50%', transform: 'translateX(-50%)', width: isCurrent ? 14 : 5, height: 4 }
    : side === 'left'
      ? { left: -7, top: '50%', transform: 'translateY(-50%)', width: 4, height: isCurrent ? 14 : 5 }
      : { right: -7, top: '50%', transform: 'translateY(-50%)', width: 4, height: isCurrent ? 14 : 5 })
  const iconSize = Math.round(size * 0.5)
  const tileBase: CSSProperties = {
    position: 'relative', width: size, height: size, flexShrink: 0, padding: 0, borderRadius: Math.round(size * 0.26),
    cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'width .09s ease-out, height .09s ease-out',
  }

  const screenTile = (item: RunNavItem, key: string, kind: 'pin' | 'run' | 'rec') => {
    const isCurrent = current?.id === item.id
    const isOpen = openIds.has(item.id)
    const label = kind === 'rec' ? `Recent: ${item.title}` : item.title
    const filled = kind === 'pin'
    return (
      <button
        key={key}
        type="button"
        className="tw-mac-tile"
        aria-label={`${label}${isCurrent ? ', on screen' : isOpen ? ', open' : ''}`}
        aria-current={isCurrent ? 'page' : undefined}
        aria-keyshortcuts={kind === 'pin' ? (vertical ? 'Alt+Shift+ArrowUp Alt+Shift+ArrowDown' : 'Alt+Shift+ArrowLeft Alt+Shift+ArrowRight') : undefined}
        {...tw('nav', item.obj_name, { type: item.obj_type, selected: isCurrent, dock: kind })}
        {...itemProps(key, kind === 'pin' && model.pinned.length > 1 ? `${item.title} · drag to reorder` : label)}
        {...(kind === 'pin' ? bind(item.id) : {})}
        onClick={() => model.open(item)}
        onContextMenu={entryMenu.onContextMenu(item)}
        onKeyDown={(e) => {
          const back = vertical ? 'ArrowUp' : 'ArrowLeft'
          const fwd = vertical ? 'ArrowDown' : 'ArrowRight'
          if (kind === 'pin' && e.altKey && e.shiftKey && (e.key === back || e.key === fwd)) {
            e.preventDefault(); e.stopPropagation()
            movePinned(item, e.key === back ? -1 : 1)
            return
          }
          entryMenu.onKeyDown(item)(e)
        }}
        data-mac-mag=""
        style={{
          ...tileBase,
          border: filled ? 'none' : `1px solid ${S.border}`,
          background: filled
            ? `linear-gradient(160deg, color-mix(in srgb, ${accent} 82%, ${S.panel}), ${accent})`
            : `color-mix(in srgb, ${accent} 14%, ${S.raised})`,
          color: filled ? '#fff' : accent,
          boxShadow: filled ? '0 2px 6px rgba(0,0,0,0.18)' : 'none',
        }}
      >
        <span data-mac-ico="" aria-hidden style={{ display: 'inline-flex', transition: 'transform .09s ease-out' }}>
          <NavIcon node={item} size={iconSize} strokeWidth={1.8} />
        </span>
        {(isOpen || isCurrent) && (
          <span aria-hidden style={{ position: 'absolute', ...dot(isCurrent), borderRadius: 2, background: isCurrent ? S.accent : S.text }} />
        )}
      </button>
    )
  }

  const thumbShell = (content: ReactNode, dirty = false): ReactNode => (
    <>
      <span aria-hidden style={{
        display: 'flex', alignItems: 'center', gap: 3, height: 11, padding: '0 6px', flexShrink: 0,
        background: S.surface2, borderBottom: `1px solid ${S.border}`,
      }}>
        {[LIGHTS.close, LIGHTS.minimise, LIGHTS.zoom].map((c) => (
          <span key={c} style={{ width: 5, height: 5, borderRadius: '50%', background: c }} />
        ))}
        <span style={{ flex: 1 }} />
        {dirty && <UnsavedDot />}
      </span>
      <span aria-hidden style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '0 6px' }}>
        {content}
      </span>
    </>
  )
  const thumbStyle: CSSProperties = {
    width: thumbW, height: size, padding: 0, flexShrink: 0, borderRadius: 8, overflow: 'hidden',
    display: 'flex', flexDirection: 'column', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
    background: S.panel, color: S.text, border: `1px solid ${S.border}`, boxShadow: '0 3px 10px rgba(0,0,0,0.18)',
  }

  const editorThumb = (s: EditorSession): ReactNode => {
    const screen = model.itemFor(s.objName)?.title || s.objName
    const title = editorTitle(s)
    const text = `${s.mode === 'add' ? 'New' : 'Editing'} ${title} · ${screen}${s.dirty ? ' · unsaved' : ''}`
    return (
      <div key={s.id} className="tw-mac-thumb" style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
        <button
          type="button"
          className="tw-mac-tile"
          aria-label={`Restore editor: ${title}, ${screen}${s.dirty ? ', unsaved changes' : ''}`}
          aria-keyshortcuts="Delete"
          {...tw('action', 'shell-editor', { obj: s.objName, mode: s.mode, txn: s.txnId, dirty: s.dirty, dock: 'editor' })}
          {...itemProps(`ed:${s.id}`, text)}
          onClick={() => model.restoreEditor(s.id)}
          onKeyDown={(e) => { if (e.key === 'Delete') { e.preventDefault(); model.closeEditor(s.id) } }}
          style={thumbStyle}
        >
          {thumbShell(
            <>
              <EditorMark session={s} model={model} size={Math.max(18, Math.min(24, size - 18))} />
              {!vertical && (
                <span style={{ flex: 1, minWidth: 0, lineHeight: 1.15 }}>
                  <span className="tw-sh-clip" style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{title}</span>
                  {size >= 52 && <span className="tw-sh-clip" style={{ display: 'block', fontSize: 13, color: S.text2 }}>{screen}</span>}
                </span>
              )}
            </>, s.dirty)}
        </button>
        <button
          type="button"
          className="tw-sh-btn tw-mac-thumb-x"
          aria-label={`Close ${title}`}
          {...tw('action', 'shell-editor-close', { obj: s.objName, txn: s.txnId, dock: 'editor' })}
          {...itemProps(`edx:${s.id}`, `Close ${title}`)}
          onClick={() => model.closeEditor(s.id)}
          style={{
            position: 'absolute', top: -9, right: -9, width: 24, height: 24, padding: 0, borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            background: S.raised, color: S.text, border: `1px solid ${S.borderStrong}`, boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          }}
        >
          <X size={13} strokeWidth={2.6} aria-hidden />
        </button>
      </div>
    )
  }

  const windowThumb = (item: RunNavItem): ReactNode => (
    <button
      key={`min:${item.id}`}
      type="button"
      className="tw-mac-tile"
      aria-label={`Restore window: ${item.title}`}
      {...tw('action', 'shell-mac-minimised', { obj: item.obj_name })}
      {...itemProps(`min:${item.id}`, `${item.title} · minimised`)}
      onClick={() => onRestoreWindow(item)}
      style={thumbStyle}
    >
      {thumbShell(
        <>
          <span style={{ display: 'inline-flex', color: accent }}><NavIcon node={item} size={Math.max(14, Math.min(18, size - 22))} /></span>
          {!vertical && <span className="tw-sh-clip" style={{ fontSize: 13, fontWeight: 600 }}>{item.title}</span>}
        </>)}
    </button>
  )

  const children: ReactNode[] = [
    <button
      key="all"
      ref={launcherRef as RefObject<HTMLButtonElement>}
      type="button"
      className="tw-mac-tile"
      data-mac-launcher=""
      aria-label="All screens"
      aria-haspopup="dialog"
      aria-expanded={allOpen}
      {...tw('action', 'shell-mac-all-screens', { selected: allOpen })}
      {...itemProps('all', 'All screens')}
      onClick={onToggleAll}
      data-mac-mag=""
      style={{ ...tileBase, border: 'none', background: S.text, color: S.panel }}
    >
      <span data-mac-ico="" aria-hidden style={{ display: 'inline-flex', transition: 'transform .09s ease-out' }}>
        <LayoutGrid size={Math.round(size * 0.46)} />
      </span>
      {allOpen && <span aria-hidden style={{ position: 'absolute', ...dot(false), borderRadius: 2, background: S.text }} />}
    </button>,
  ]
  if (model.pinned.length) {
    children.push(sep('s-pin'))
    model.pinned.forEach((e) => children.push(screenTile(e.item, `pin:${e.item.id}`, 'pin')))
  }
  if (running.length || recent.length) {
    children.push(sep('s-run'))
    running.forEach((i) => children.push(screenTile(i, `run:${i.id}`, 'run')))
    recent.forEach((e) => children.push(screenTile(e.item, `rec:${e.item.id}`, 'rec')))
  }
  if (minimised.length || editors.length) {
    children.push(sep('s-ed'))
    minimised.forEach((i) => children.push(windowThumb(i)))
    editors.forEach((s) => children.push(editorThumb(s)))
  }

  // Magnified tiles grow away from the edge: up from a bottom Dock, inwards
  // from a side one.
  const placement: CSSProperties = side === 'bottom'
    ? { left: '50%', bottom: 8, transform: 'translateX(-50%)', flexDirection: 'row', alignItems: 'flex-end',
      maxWidth: 'calc(100% - 120px)', padding: '10px 12px 12px' }
    : { top: '50%', transform: 'translateY(-50%)', [side]: 8, flexDirection: 'column',
      alignItems: side === 'left' ? 'flex-start' : 'flex-end', maxHeight: 'calc(100% - 40px)', padding: '12px 10px' } as CSSProperties

  return (
    <>
      {moveTarget && (
        // Where the Dock will land: its three edges, the target lit.
        <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 939, pointerEvents: 'none' }}>
          {DOCK_SIDES.map((d) => {
            const on = d.id === moveTarget
            return (
              <div key={d.id} style={{
                position: 'absolute', borderRadius: 16, transition: 'background .12s, border-color .12s, opacity .12s',
                opacity: on ? 1 : 0.55,
                background: on ? glass(S.accent, 26) : glass(S.panel, 24),
                border: `2px dashed ${on ? S.accent : glass(S.borderStrong, 80)}`,
                ...(d.id === 'bottom' ? { left: '22%', right: '22%', bottom: 6, height: size + 26 }
                  : { top: '16%', bottom: '16%', width: size + 26, [d.id]: 6 }),
              }} />
            )
          })}
        </div>
      )}
      <div
        key={side}
        ref={ref}
        role="toolbar"
        aria-label="Dock"
        aria-orientation={vertical ? 'vertical' : 'horizontal'}
        onKeyDown={onKeyDown}
        onPointerEnter={() => { if (magOn) measureRest() }}
        onPointerLeave={() => { if (!moveRef.current) { resetMag(); setTip(null) } }}
        onPointerDownCapture={onPointerDownCapture}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => endMove(true)}
        onPointerCancel={() => endMove(false)}
        onContextMenu={(e) => {
          if ((e.target as HTMLElement).closest('button')) return
          e.preventDefault()
          setMenu({ x: e.clientX, y: e.clientY })
        }}
        {...tw('action', 'shell-mac-dock', { side, size })}
        style={{
          position: 'absolute', zIndex: 940, display: 'flex', gap: GAP, ...placement,
          boxSizing: 'border-box', touchAction: 'none',
          overflow: overflowing ? 'auto' : 'visible', scrollbarWidth: 'thin',
          cursor: moveTarget ? 'grabbing' : 'grab',
          borderRadius: 22, background: glass(S.panel, 58), border: `1px solid ${glass(S.borderStrong, 70)}`,
          backdropFilter: 'blur(26px) saturate(180%)', WebkitBackdropFilter: 'blur(26px) saturate(180%)',
          boxShadow: moveTarget ? '0 18px 44px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.18)',
          animation: reduceMotion ? undefined : 'tw-mac-dock-in .22s ease',
        }}
      >
        {children}
      </div>
      {menu && (
        <DockMenu x={menu.x} y={menu.y} side={side} magnify={magnify} size={size}
          canCloseAll={model.openScreens.length > 0}
          onSide={(s) => { setMenu(null); onSide(s) }} onMagnify={(on) => { setMenu(null); onMagnify(on) }}
          onSize={onSize} onCloseAll={() => { setMenu(null); onCloseAll() }}
          onSettings={() => { setMenu(null); onSettings() }} onClose={() => setMenu(null)} />
      )}
      {tip && !moveTarget && !reordering && (
        <span ref={tipRef} aria-hidden style={{
          position: 'fixed', left: -9999, top: -9999, zIndex: 9443, pointerEvents: 'none',
          transform: side === 'bottom' ? 'translate(-50%, -100%)' : side === 'left' ? 'translateY(-50%)' : 'translate(-100%, -50%)',
          padding: '5px 10px', borderRadius: 7, whiteSpace: 'nowrap',
          background: S.text, color: S.panel, fontSize: 13, lineHeight: 1.4, boxShadow: '0 6px 18px rgba(0,0,0,0.22)',
        }}>
          {tip.text}
        </span>
      )}
      {entryMenu.ui}
    </>
  )
}

/** Right-click on the Dock: position, size, magnification, close all, settings. */
function DockMenu({ x, y, side, magnify, size, canCloseAll, onSide, onMagnify, onSize, onCloseAll, onSettings, onClose }: {
  x: number; y: number; side: DockSide; magnify: boolean; size: number; canCloseAll: boolean
  onSide: (s: DockSide) => void; onMagnify: (on: boolean) => void; onSize: (px: number) => void
  onCloseAll: () => void; onSettings: () => void; onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus()
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose() }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [onClose])
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    // The size slider keeps its own arrow keys.
    if ((e.target as HTMLElement).tagName === 'INPUT' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) return
    const els = [...(ref.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled), input[type="range"]') || [])]
    const i = els.indexOf(document.activeElement as HTMLElement)
    const go = (j: number) => { e.preventDefault(); els[(j + els.length) % els.length]?.focus() }
    if (e.key === 'ArrowDown') go(i + 1)
    else if (e.key === 'ArrowUp') go(i - 1)
    else if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); onClose() }
  }
  const row = {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 36, padding: '0 10px',
    border: 'none', borderRadius: 7, background: 'transparent', color: S.text, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 14, textAlign: 'left',
  } as const
  const head = (t: string) => <div role="presentation" style={{ padding: '6px 10px 2px', fontSize: 13, fontWeight: 600, color: S.text2 }}>{t}</div>
  const sep = <div role="separator" style={{ height: 1, margin: '4px 6px', background: S.border }} />
  return (
    <div ref={ref} role="menu" aria-label="Dock" onKeyDown={onKey} data-tw-shell-menu=""
      style={{ ...menuPanel, minWidth: 260, position: 'fixed', zIndex: 9444,
        left: Math.max(8, Math.min(x, window.innerWidth - 280)), top: Math.max(8, Math.min(y, window.innerHeight - 360)) }}>
      {head('Position on screen')}
      {DOCK_SIDES.map((d) => (
        <button key={d.id} type="button" role="menuitemradio" aria-checked={d.id === side} className="tw-sh-item"
          {...tw('action', `shell-mac-dock-side:${d.id}`)} onClick={() => onSide(d.id)} style={row}>
          <span aria-hidden style={{ width: 14, display: 'inline-flex' }}>{d.id === side && <Check size={14} />}</span>{d.label}
        </button>
      ))}
      {sep}
      <label role="presentation" style={{ display: 'block', padding: '4px 10px 8px', fontSize: 14, color: S.text }}>
        <span style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          Size <span style={{ color: S.text2, fontSize: 13 }}>{size}px</span>
        </span>
        <input type="range" min={DOCK_SIZE_MIN} max={DOCK_SIZE_MAX} step={2} value={size}
          onChange={(e) => onSize(Number(e.target.value))} aria-label="Dock size"
          {...tw('field', 'shell-mac-dock-size')} style={{ width: '100%' }} />
      </label>
      <button type="button" role="menuitemcheckbox" aria-checked={magnify} className="tw-sh-item"
        {...tw('action', 'shell-mac-magnify')} onClick={() => onMagnify(!magnify)} style={row}>
        <span aria-hidden style={{ width: 14, display: 'inline-flex' }}>{magnify && <Check size={14} />}</span>Magnification
      </button>
      {sep}
      <button type="button" role="menuitem" className="tw-sh-item" disabled={!canCloseAll}
        {...tw('action', 'shell-mac-dock-close-all')} onClick={onCloseAll} style={{ ...row, opacity: canCloseAll ? 1 : 0.5 }}>
        <span aria-hidden style={{ width: 14 }} />Close all windows
      </button>
      <button type="button" role="menuitem" className="tw-sh-item" {...tw('action', 'shell-mac-settings')}
        onClick={onSettings} style={row}>
        <span aria-hidden style={{ width: 14 }} />Desktop &amp; Dock settings…
      </button>
    </div>
  )
}
